import { yupResolver } from "@hookform/resolvers/yup";
import { SelectChangeEvent } from "@mui/material";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useGetClientByIdMutation,
  useGetInvoicingLanguages,
} from "../../../../../../../../api/services/client/clientApis";
import { POData } from "../../../../../../../../api/services/common/models";
import {
  useGetParticipantDetailsById,
  useGetPaymentTermByProgramId,
  useGetPaymentTerms,
  useGetProgramDetailById,
} from "../../../../../../../../api/services/program/programApis";
import { ReactComponent as DownloadIcon } from "../../../../../../../../assets/images/download.svg";
import { ReactComponent as WarningIcon } from "../../../../../../../../assets/images/warning-icon.svg";
import { useGeneratePDF } from "../../../../../../../../common/hooks/useGeneratePDF";
import { InformationBadge } from "../../../../../../../../common/informationBadge/informationBadge";
import {
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiSelect,
  MuiTextField,
  MuiTypography,
} from "../../../../../../../../components";
import PermissionWrapper from "../../../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  BE_ERROR_CODES,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_30,
  MODULE_PERMISSIONS,
  SITE_CORE_COUNTRIES_VALUE,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  getCurrencyNameAndLocaleById,
  getFormattedAmount,
  getFullName,
  getValueOrBlank,
  getValueOrDash,
  getValueOrZero,
  isEmpty,
  isErrorCode,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { currencyListSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  programBillingCountryIdSelector,
  programSelector,
} from "../../../../../../../../redux/slices/program/programSelector";
import {
  FeesAndExpenseRow,
  RaiseInvoicePODetailsFormKey,
} from "../../../../../../modelsAndSchemas/Models";
import { raiseInvoiceSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { RaiseInvoiceModalSchema } from "./modelsAndSchema/ValidationSchemas";
import { ProformaPdfTemplate } from "./ProformaPdfTemplate";
import { RaiseInvoiceList } from "./RaiseInvoiceList";
import { ModalContainer } from "./RaiseInvoiceModal.styles";

interface CaptureValuesParams {
  invoiceDate: string;
  paymentTerms: string;
  poNumber: string;
  localInvoiceNumber: string;
}

type RaiseInvoiceModalProps = {
  open: boolean;
  modalTitle: string;
  totalBillingAmount: number;
  totalTaxAmount: number;
  totalInvoiceAmount: number;
  totalInvoiceCurrency: string;
  invoiceFeesAndExpense: FeesAndExpenseRow[];
  programId: string;
  poNumber?: string;
  isRaiseInvoiceError: boolean;
  raiseInvoiceError: Error | null;
  onClose: () => void;
  onPrimaryButtonClick: () => void;
  captureValues?: (params: CaptureValuesParams) => void;
};

const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };

export const RaiseInvoice: FC<RaiseInvoiceModalProps> = ({
  open,
  onClose,
  modalTitle,
  totalBillingAmount,
  totalTaxAmount,
  totalInvoiceAmount,
  totalInvoiceCurrency,
  invoiceFeesAndExpense,
  programId,
  poNumber,
  onPrimaryButtonClick,
  captureValues,
  isRaiseInvoiceError,
  raiseInvoiceError,
}) => {
  const { fetchLabel, commonLabels, commonValidationMessages } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { generatePDF } = useGeneratePDF();
  const { displayToast } = useToast();
  const { clientId, programTemplateId } = useSelector(programSelector);
  const billingCountryId = useSelector(programBillingCountryIdSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const {
    mutateAsync: getClientById,
    data: clientData,
    isPending: isGetClientByIdPending,
    isSuccess: isGetClientByIdSuccess,
  } = useGetClientByIdMutation();

  const {
    data: programDetails,
    isSuccess: isGetProgramDetailSuccess,
    isFetching: isViewProgramDetailFetching,
    refetchWithParams: refetchViewProgramDetail,
  } = useGetProgramDetailById();

  const {
    data: participantDetails,
    isFetching: isPendingParticipantDetails,
    refetchWithParams: getParticipantDetailsByProgramId,
    isSuccess: isGetParticipantDetailsSuccess,
  } = useGetParticipantDetailsById();

  const {
    mutateAsync: getGetPaymentTerms,
    data: paymentTermsList,
    isPending: isPaymentTermsListPending,
  } = useGetPaymentTerms();

  const {
    mutateAsync: getGetPaymentTermByProgramId,
    data: paymentTermByProgramId,
    isSuccess: isPaymentTermByProgramIdSuccess,
    isPending: isPaymentTermByProgramIdPending,
  } = useGetPaymentTermByProgramId();

  const {
    data: invoicingLanguages,
    refetchWithParams: refetchInvoicingLanguages,
  } = useGetInvoicingLanguages();

  const {
    control,
    watch,
    getValues,
    setValue,
    handleSubmit,
    formState,
    clearErrors,
    setError,
  } = useForm({
    mode: "all",
    defaultValues: {
      [RaiseInvoicePODetailsFormKey.localInvoiceNumber]: "",
      [RaiseInvoicePODetailsFormKey.poNumber]: poNumber || "",
      [RaiseInvoicePODetailsFormKey.currentPOBalance]: "",
      [RaiseInvoicePODetailsFormKey.poShortage]: "",
      [RaiseInvoicePODetailsFormKey.invoiceDate]: dayjs().format(
        userCountryDateFormat,
      ),
      [RaiseInvoicePODetailsFormKey.paymentTerms]: "",
    },
    resolver: yupResolver(RaiseInvoiceModalSchema(commonValidationMessages)),
  });
  const pageId = "raise-invoice-modal";
  const [currentPOBalance, setCurrentPOBalance] = useState<number>(0);
  const [showBottomError, setShowBottomError] = useState<boolean>(false);
  const [bottomErrorMessage, setBottomErrorMessage] = useState<string>("");

  const purchaseOrder: POData = {
    id: getValueOrBlank(programDetails?.purchaseOrderId),
    name: getValueOrBlank(programDetails?.poNumber?.name),
    currentAmount: getValueOrZero(programDetails?.currentPOAmount),
  };

  const dispatch = useDispatch();

  useEffect(() => {
    if (programId) {
      refetchViewProgramDetail(programId);
      refetchInvoicingLanguages({
        programId: programId,
      });
      getGetPaymentTermByProgramId(programId);
    }
  }, [programId]);

  useEffect(() => {
    getGetPaymentTerms();
  }, []);
  useEffect(() => {
    const { poNumber } = getValues();
    if (poNumber && purchaseOrder) {
      setCurrentPOBalance(Number(purchaseOrder?.currentAmount ?? 0));
    }
  }, [
    watch(RaiseInvoicePODetailsFormKey.poNumber),
    watch(RaiseInvoicePODetailsFormKey.paymentTerms),
    watch(RaiseInvoicePODetailsFormKey.invoiceDate),
    purchaseOrder,
  ]);

  useEffect(() => {
    if (hasPOShortage()) {
      setShowBottomError(true);
      setBottomErrorMessage(getLabel("poShortageMessage"));
    } else if (hasEmptyPOAmount()) {
      setShowBottomError(true);
      setBottomErrorMessage(getLabel("poNoAmountMessage"));
    } else {
      setShowBottomError(false);
      setBottomErrorMessage("");
    }
  }, [
    watch(RaiseInvoicePODetailsFormKey.poNumber),
    programDetails?.currentPOAmount,
    currentPOBalance,
    totalInvoiceAmount,
  ]);

  const hasEmptyPOAmount = () => {
    const { poNumber } = getValues();
    return poNumber && isEmpty(programDetails?.currentPOAmount);
  };

  const hasPOShortage = () => {
    const { poNumber } = getValues();
    return (
      isNotEmpty(programDetails?.currentPOAmount) &&
      Boolean(poNumber && validatePOBalance())
    );
  };
  useEffect(() => {
    if (isRaiseInvoiceError && raiseInvoiceError) {
      const isErrorCodeSame = isErrorCode(
        raiseInvoiceError,
        BE_ERROR_CODES.VALID_ACCOUNTING_PERIOD_NOT_DEFINED,
      );
      if (isErrorCodeSame) {
        setError(RaiseInvoicePODetailsFormKey.invoiceDate, {
          type: "custom",
          message: fetchLabel(
            raiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
            raiseInvoiceSiteCoreKeys.invoiceDateMustBeWithinTheAccountingPeriod,
          ),
        });
      } else {
        displayToast(
          commonLabels.someThingWentWrongPleaseTryAgainLater,
          "error",
        );
      }
    }
  }, [isRaiseInvoiceError, raiseInvoiceError]);

  useEffect(() => {
    if (isPaymentTermByProgramIdSuccess && isNotEmpty(paymentTermByProgramId)) {
      setValue(
        RaiseInvoicePODetailsFormKey.paymentTerms,
        paymentTermByProgramId?.id,
      );
    }
  }, [isPaymentTermByProgramIdSuccess, paymentTermByProgramId]);

  useEffect(() => {
    const { poNumber, invoiceDate, paymentTerms, localInvoiceNumber } =
      getValues();
    if (poNumber && purchaseOrder) {
      setCurrentPOBalance(Number(purchaseOrder?.currentAmount ?? 0));
    } else {
      setCurrentPOBalance(0);
    }
    if (captureValues) {
      captureValues({
        invoiceDate: getValueOrBlank(invoiceDate),
        paymentTerms: getValueOrBlank(paymentTerms),
        poNumber: getValueOrBlank(poNumber),
        localInvoiceNumber: getValueOrBlank(localInvoiceNumber),
      });
    }
  }, [
    watch(RaiseInvoicePODetailsFormKey.poNumber),
    watch(RaiseInvoicePODetailsFormKey.invoiceDate),
    watch(RaiseInvoicePODetailsFormKey.paymentTerms),
    watch(RaiseInvoicePODetailsFormKey.localInvoiceNumber),
    purchaseOrder,
  ]);

  useEffect(() => {
    const isLoading =
      isGetClientByIdPending ||
      isViewProgramDetailFetching ||
      isPendingParticipantDetails ||
      isPaymentTermsListPending ||
      isPaymentTermByProgramIdPending;
    dispatch(
      toggleLoader({
        isVisible: isLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isGetClientByIdPending,
    isViewProgramDetailFetching,
    isPendingParticipantDetails,
    isPaymentTermsListPending,
  ]);

  const poNumberList = useMemo(() => {
    if (isNotEmpty(purchaseOrder)) {
      return [
        {
          label: purchaseOrder.name,
          value: purchaseOrder.id,
        },
      ];
    }
    return [];
  }, [purchaseOrder]);

  const getLabel = (key: keyof typeof raiseInvoiceSiteCoreKeys) => {
    return fetchLabel(
      raiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
      raiseInvoiceSiteCoreKeys[key],
    );
  };

  const validatePOBalance = () => {
    if (getValues(RaiseInvoicePODetailsFormKey.poNumber) === "") return false;
    return totalInvoiceAmount > currentPOBalance;
  };

  useEffect(() => {
    if (
      isGetClientByIdSuccess &&
      isGetProgramDetailSuccess &&
      isGetParticipantDetailsSuccess &&
      isNotEmpty(clientData?.data) &&
      isNotEmpty(programDetails) &&
      isNotEmpty(participantDetails)
    ) {
      const fileName = `Proforma_Invoice_${programTemplateId}_${new Date().toLocaleDateString()}.pdf`;
      const {
        mailingAddress,
        profile: { clientName },
      } = clientData.data;
      const alternateDisplayName = getValueOrBlank(
        programDetails?.alternateDisplayName,
      );
      const billingEmail = getValueOrBlank(programDetails?.billingEmail);
      const programTemplate = getValueOrBlank(programDetails?.programTemplate);
      const programName =
        alternateDisplayName && alternateDisplayName !== EMPTY_VALUE_DASH
          ? alternateDisplayName
          : programTemplate;
      const { firstName, lastName } = participantDetails;
      const fullName = getFullName(firstName, lastName);
      const paymentTermsId = getValues(
        RaiseInvoicePODetailsFormKey.paymentTerms,
      );
      const paymentTerms = paymentTermsList?.find(
        (term) => term.id === paymentTermsId,
      )?.name;
      const { header, footer1, footer2, footer3 } = programDetails;

      generatePDF(
        <ProformaPdfTemplate
          programName={programName}
          billingEmail={billingEmail}
          mailingAddress={mailingAddress}
          clientName={clientName}
          expenseDetails={invoiceFeesAndExpense}
          participantName={fullName}
          totalInvoiceAmount={totalInvoiceAmount}
          billingCurrency={totalInvoiceCurrency}
          paymentTerms={paymentTerms}
          header={header}
          footer1={footer1}
          footer2={footer2}
          footer3={footer3}
        />,
        fileName,
      );
    }
  }, [
    isGetClientByIdSuccess,
    isGetProgramDetailSuccess,
    isGetParticipantDetailsSuccess,
    clientData,
    programDetails,
    participantDetails,
    paymentTermsList,
  ]);

  const onClickDownloadProformaInvoice = async () => {
    getClientById(clientId);
    refetchViewProgramDetail(programId);
    getParticipantDetailsByProgramId(programId);
  };

  const downloadProformaInvoice = () => (
    <ModalContainer>
      <PermissionWrapper
        permission={MODULE_PERMISSIONS.PROGRAM_VIEW_INVOICE_HISTORY}
      >
        <MuiBox className="download-performa-invoice">
          <DownloadIcon fontSize="small" className="download-icon" />
          <MuiTypography
            variant="h4Bold"
            onClick={onClickDownloadProformaInvoice}
          >
            {getLabel("downloadProformaInvoiceLinkText")}
          </MuiTypography>
        </MuiBox>
      </PermissionWrapper>
    </ModalContainer>
  );

  const currencyList = useSelector(currencyListSelector);

  const getFormattedValue = (value: number | undefined): string => {
    const currencyData = getCurrencyNameAndLocaleById(
      currencyList,
      totalInvoiceCurrency,
    );

    const formattedAmount = value
      ? `${totalInvoiceCurrency} ${getFormattedAmount(value, currencyData?.currencyLocale)}`
      : EMPTY_VALUE_DASH;
    return formattedAmount;
  };

  const handleModalClose = () => {
    clearErrors();
    onClose();
  };

  return (
    <MuiModal
      open={open}
      onClose={handleModalClose}
      title={modalTitle}
      hasLargeContent
      disablePrimaryButton={!!Object.keys(formState.errors).length}
      primaryButtonTitle={
        validatePOBalance() ? commonLabels.ok : getLabel("invoiceButton")
      }
      onPrimaryButtonClick={
        validatePOBalance()
          ? handleModalClose
          : () => {
              handleSubmit(onPrimaryButtonClick)();
            }
      }
      secondaryButtonTitle={validatePOBalance() ? "" : commonLabels.cancel}
      onSecondaryButtonClick={handleModalClose}
      headerChildren={downloadProformaInvoice()}
    >
      <ModalContainer>
        <MuiBox className="fields-wrapper">
          <MuiTypography variant="h2Bold">
            {getLabel("summarySectionHeader")}
          </MuiTypography>
          <MuiGrid container spacing={2}>
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                label={getLabel("summaryTotalAmount")}
                replaceEmptyWithDash
                value={getFormattedValue(totalBillingAmount)}
              />
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                label={getLabel("summaryTotalTaxAmount")}
                replaceEmptyWithDash
                value={getFormattedValue(totalTaxAmount)}
              />
            </MuiGrid>
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                label={getLabel("summaryTotalInvoiceAmount")}
                replaceEmptyWithDash
                value={`${getFormattedValue(totalInvoiceAmount)}`}
              />
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
        <MuiBox className="fee-and-expense-label">
          <MuiTypography variant="h3SemiBold">
            {getLabel("feeAndExpensesSectionHeader")}
          </MuiTypography>
        </MuiBox>
        <RaiseInvoiceList
          invoiceFeesAndExpense={invoiceFeesAndExpense}
          currencyName={totalInvoiceCurrency}
        />
        <MuiBox className="po-details-wrapper">
          <MuiBox className="po-details-header">
            <MuiTypography variant="h2Bold">
              {getLabel("invoiceAndPODetailsSectionHeader")}
            </MuiTypography>
          </MuiBox>
          <MuiBox className="po-details-content">
            <MuiGrid container spacing={2}>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={RaiseInvoicePODetailsFormKey.localInvoiceNumber}
                >
                  <MuiTextField
                    name={RaiseInvoicePODetailsFormKey.localInvoiceNumber}
                    id={`${pageId}-${RaiseInvoicePODetailsFormKey.localInvoiceNumber}`}
                    label={getLabel("localInvoiceNumber")}
                    requiredARIALabel={commonARIALabel.required}
                    placeholder={
                      billingCountryId === SITE_CORE_COUNTRIES_VALUE.FRANCE
                        ? EMPTY_VALUE_DASH
                        : commonLabels.inputPlaceHolder
                    }
                    value=""
                    shouldTrimSpaces
                    maxLength={MAX_LENGTH_30}
                    showCloseIcon
                    disabled={
                      billingCountryId === SITE_CORE_COUNTRIES_VALUE.FRANCE
                    }
                  />
                </FormController>
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiDateFieldFormController
                  control={control}
                  name={RaiseInvoicePODetailsFormKey.invoiceDate}
                  id={`${pageId}-${RaiseInvoicePODetailsFormKey.invoiceDate}`}
                  value={
                    getValues(RaiseInvoicePODetailsFormKey.invoiceDate)
                      ? dayjs(
                          getValues(RaiseInvoicePODetailsFormKey.invoiceDate),
                          userCountryDateFormat,
                        )
                      : dayjs()
                  }
                  label={getLabel("invoiceDate")}
                  className="date-picker"
                  disablePast
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                  selectDateARIALabel={commonARIALabel.selectDate}
                  format={userCountryDateFormat}
                />
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiTextField
                  name={RaiseInvoicePODetailsFormKey.invoiceLanguage}
                  id={`${pageId}-${RaiseInvoicePODetailsFormKey.invoiceLanguage}`}
                  value={getValueOrDash(invoicingLanguages?.[0]?.name)}
                  label={getLabel("invoiceLanguage")}
                  disabled={true}
                  requiredARIALabel={commonARIALabel.required}
                />
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={RaiseInvoicePODetailsFormKey.paymentTerms}
                  key={RaiseInvoicePODetailsFormKey.paymentTerms}
                >
                  <MuiSelect
                    label={getLabel("paymentTermsDropDown")}
                    options={setApiDataToDropDownOption(paymentTermsList ?? [])}
                    value={""}
                    id={`${pageId}-paymentTerms`}
                    isMandatory
                    onChange={(e: SelectChangeEvent<unknown>) => {
                      setValue(
                        RaiseInvoicePODetailsFormKey.paymentTerms,
                        e.target.value as string,
                      );
                    }}
                    requiredARIALabel={commonARIALabel.required}
                    placeholder={commonLabels.selectPlaceHolder}
                    showSearch
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>
            <MuiGrid container spacing={2}>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={RaiseInvoicePODetailsFormKey.poNumber}
                  key={RaiseInvoicePODetailsFormKey.poNumber}
                >
                  <MuiSelect
                    label={getLabel("poNumber")}
                    options={poNumberList || []}
                    visibleDefaultSelectOption={true}
                    value={""}
                    id={`${pageId}-${RaiseInvoicePODetailsFormKey.poNumber}`}
                    placeholder={commonLabels.selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              {getValues(RaiseInvoicePODetailsFormKey.poNumber) && (
                <>
                  <MuiGrid size={gridSize} className="client-code">
                    <FormController
                      control={control}
                      name={RaiseInvoicePODetailsFormKey.currentPOBalance}
                      key={RaiseInvoicePODetailsFormKey.currentPOBalance}
                    >
                      <MuiTextField
                        label={getLabel("currentPOBalance")}
                        value={currentPOBalance}
                        id={`${pageId}-${RaiseInvoicePODetailsFormKey.currentPOBalance}`}
                        placeholder={
                          hasEmptyPOAmount()
                            ? EMPTY_VALUE_DASH
                            : `${totalInvoiceCurrency} ${currentPOBalance.toString()}`
                        }
                        disabled
                        requiredARIALabel={commonARIALabel.required}
                        icon={
                          hasEmptyPOAmount() && (
                            <WarningIcon className="po-shortage-icon" />
                          )
                        }
                      />
                    </FormController>
                  </MuiGrid>
                  {hasPOShortage() && (
                    <MuiGrid size={gridSize} className="client-code">
                      <FormController
                        control={control}
                        name={RaiseInvoicePODetailsFormKey.poShortage}
                        key={RaiseInvoicePODetailsFormKey.poShortage}
                      >
                        <MuiTextField
                          label={getLabel("poShortage")}
                          value={String(
                            (totalInvoiceAmount - currentPOBalance).toFixed(2),
                          )}
                          id={`${pageId}-${RaiseInvoicePODetailsFormKey.poShortage}`}
                          placeholder={String(
                            (totalInvoiceAmount - currentPOBalance).toFixed(2),
                          )}
                          disabled
                          icon={<WarningIcon className="po-shortage-icon" />}
                          requiredARIALabel={commonARIALabel.required}
                        />
                      </FormController>
                    </MuiGrid>
                  )}
                </>
              )}
            </MuiGrid>
          </MuiBox>
          {showBottomError && (
            <MuiBox className="additional-info">
              <InformationBadge
                icon={<WarningIcon className="warning-icon" />}
                position="start"
                message={bottomErrorMessage}
              />
            </MuiBox>
          )}
        </MuiBox>
      </ModalContainer>
    </MuiModal>
  );
};
